import { useEffect, useState } from 'react'

import { api } from '../api'
import { useAuth } from '../auth'
import { checkServer, getServerUrl, isDesktop, needsServerSetup, setServerUrl } from '../lib/server'

export default function Login() {
  const { login, loginWithToken } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  // Настройка адреса сервера. В десктопе без неё войти некуда, поэтому при
  // первом запуске экран открывается сразу на ней.
  const [serverOpen, setServerOpen] = useState(needsServerSetup())
  const [server, setServer] = useState(getServerUrl() || 'http://')
  const [serverState, setServerState] = useState<'idle' | 'checking' | 'ok' | 'fail'>('idle')
  const [serverError, setServerError] = useState('')

  // Вход по одноразовому коду. Показываем, только если сервер умеет его слать —
  // иначе кнопка вела бы в тупик.
  const [byCode, setByCode] = useState(false)
  const [codeChannels, setCodeChannels] = useState<{ email: boolean; sms: boolean } | null>(null)
  const [destination, setDestination] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)

  useEffect(() => {
    api.codeChannels().then(setCodeChannels).catch(() => setCodeChannels(null))
  }, [])

  const codeAvailable = !!codeChannels && (codeChannels.email || codeChannels.sms)
  const codeHint = !codeChannels
    ? ''
    : codeChannels.email && codeChannels.sms
      ? 'Почта или телефон'
      : codeChannels.email
        ? 'Рабочая почта'
        : 'Телефон'

  async function askCode(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await api.requestCode(destination)
      setCodeSent(true)
    } catch (err: any) {
      setError(err?.message ?? 'Не удалось отправить код')
    } finally {
      setBusy(false)
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      const res = await api.verifyCode(destination, code)
      await loginWithToken(res.access_token, res.user)
    } catch (err: any) {
      setError(err?.message ?? 'Не удалось войти')
    } finally {
      setBusy(false)
    }
  }

  async function saveServer(e: React.FormEvent) {
    e.preventDefault()
    setServerState('checking')
    setServerError('')
    const problem = await checkServer(server)
    if (problem) {
      setServerState('fail')
      setServerError(problem)
      return
    }
    setServerUrl(server)
    setServerState('ok')
    setServerOpen(false)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await login(email, password)
    } catch (err: any) {
      setError(err?.message ?? 'Не удалось войти')
    } finally {
      setBusy(false)
    }
  }

  if (serverOpen) {
    return (
      <div className="login-page">
        <form className="login-box" onSubmit={saveServer}>
          <img src="/brand/logo-full.png" alt="Бизнес Эра" className="login-logo" />
          <h1>Подключение</h1>
          <p className="hint">Укажите адрес сервера, на котором работает программа</p>

          {serverError && <div className="error">{serverError}</div>}

          <div className="field">
            <label htmlFor="server">Адрес сервера</label>
            <input
              id="server"
              value={server}
              onChange={(e) => {
                setServer(e.target.value)
                setServerState('idle')
              }}
              placeholder="http://192.168.1.10:8000"
              autoFocus
            />
            <span className="hint" style={{ marginTop: 4 }}>
              Спросите у администратора. Обычно это адрес офисного компьютера,
              например <code>http://192.168.1.10:8000</code>
            </span>
          </div>

          <button
            className="btn primary"
            style={{ width: '100%', marginTop: 8 }}
            disabled={serverState === 'checking'}
          >
            {serverState === 'checking' ? 'Проверяем…' : 'Проверить и сохранить'}
          </button>

          {getServerUrl() && (
            <button
              type="button"
              className="link-inline"
              style={{ marginTop: 10 }}
              onClick={() => setServerOpen(false)}
            >
              Отмена
            </button>
          )}
        </form>
      </div>
    )
  }

  if (byCode) {
    return (
      <div className="login-page">
        <form className="login-box" onSubmit={codeSent ? submitCode : askCode}>
          <img src="/brand/logo-full.png" alt="Бизнес Эра" className="login-logo" />
          <h1>Вход по коду</h1>
          <p className="hint">
            {codeSent ? 'Введите код, который мы отправили' : `${codeHint} — пришлём код`}
          </p>

          {error && <div className="error">{error}</div>}

          <div className="field">
            <label htmlFor="dest">{codeHint}</label>
            <input
              id="dest"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder={codeChannels?.sms ? '+7 999 123-45-67' : 'ivanov@example.ru'}
              disabled={codeSent}
              autoFocus
              required
            />
          </div>

          {codeSent && (
            <div className="field">
              <label htmlFor="code">Код из сообщения</label>
              <input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                autoFocus
                required
              />
            </div>
          )}

          <button className="btn primary" style={{ width: '100%', marginTop: 8 }} disabled={busy}>
            {busy ? 'Подождите…' : codeSent ? 'Войти' : 'Получить код'}
          </button>

          {codeSent && (
            <button
              type="button"
              className="link-inline"
              style={{ marginTop: 10 }}
              onClick={() => {
                setCodeSent(false)
                setCode('')
                setError('')
              }}
            >
              Отправить код заново
            </button>
          )}

          <button
            type="button"
            className="link-inline"
            style={{ marginTop: 10 }}
            onClick={() => {
              setByCode(false)
              setCodeSent(false)
              setError('')
            }}
          >
            Войти по паролю
          </button>
        </form>
      </div>
    )
  }

  return (
    <div className="login-page">
      <form className="login-box" onSubmit={submit}>
        <img src="/brand/logo-full.png" alt="Бизнес Эра" className="login-logo" />
        <h1>Учёт времени</h1>
        <p className="hint">Вход для технических специалистов</p>

        {error && <div className="error">{error}</div>}

        <div className="field">
          <label htmlFor="login">Логин</label>
          <input
            id="login"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
        </div>

        <div className="field">
          <label htmlFor="password">Пароль</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        <button className="btn primary" style={{ width: '100%', marginTop: 8 }} disabled={busy}>
          {busy ? 'Вход…' : 'Войти'}
        </button>

        {codeAvailable && (
          <button
            type="button"
            className="link-inline"
            style={{ marginTop: 12 }}
            onClick={() => {
              setByCode(true)
              setError('')
            }}
          >
            Войти по коду из {codeChannels?.email ? 'почты' : 'SMS'}
          </button>
        )}

        {isDesktop && (
          <button
            type="button"
            className="link-inline"
            style={{ marginTop: 12 }}
            onClick={() => setServerOpen(true)}
          >
            Сервер: {getServerUrl() || 'не задан'}
          </button>
        )}
      </form>
    </div>
  )
}
